import { DocumentTypes } from '@urbanx/agx.campaigns.client';
import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, FormConfigType, AustralianState } from '@urbanx/agx-ui-components';
import { useAppDispatch } from './useAppDispatch';
import { CampaignsApiContext, FormType } from '@urbanx/agx.campaigns.client';
import { setAndShowErrorToast } from '../store/config';
import { loadFormConfig } from '../features/form/formReducer';
import { loadFormMarketing } from 'Api/Marketing/marketingApi';
import { AuthContext } from '@urbanx/agx.auth.client';

export const useFormLoader = () => {
  const { getAuthToken } = useContext(AuthContext);
  const campaigns = useContext(CampaignsApiContext);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const errorMessage = 'Failed to load form';
  const loadForm = useCallback(
    async (campaignId: string, formId: string, formConfigType: FormConfigType) => {
      if (!getAuthToken) return;

      const authToken = await getAuthToken();

      if (!authToken) {
        dispatch(setAndShowErrorToast(errorMessage));
        return;
      }
      let form: Form | null = null;
      switch (formConfigType) {
        case FormConfigType.Campaign: {
          const loaded = await campaigns.loadForm(campaignId, formId);
          form = {
            ...loaded,
            state: AustralianState[loaded.state],
            pageStatuses: loaded.pageStatuses ?? [],
            documents: loaded.documents.map((document) => ({
              ...document,
              documentType: DocumentTypes[document.documentType],
            })),
            formConfigType: FormConfigType.Campaign,
          };
          break;
        }
        case FormConfigType.Marketing: {
          form = await loadFormMarketing(authToken, campaignId, formId);
          break;
        }
      }

      if (!form) {
        dispatch(setAndShowErrorToast(errorMessage));
        return;
      }

      dispatch(loadFormConfig(form));
      navigate(
        form.formType === FormType.ListingProposal
          ? `/form?${new URLSearchParams({ campaignId: form.campaignId, formId: form.formId })}`
          : '/form'
      );
    },
    [getAuthToken, campaigns, dispatch, navigate]
  );

  const loadFormWithConfig = useCallback(
    async (form: Form) => {
      dispatch(loadFormConfig(form));
      navigate(
        form.formType === FormType.ListingProposal
          ? `/form?${new URLSearchParams({ campaignId: form.campaignId, formId: form.formId })}`
          : '/form'
      );
    },
    [dispatch, navigate]
  );

  return {
    loadForm,
    loadFormWithConfig,
  };
};
